/**
 * Educational storefront theming.
 *
 * A clinic's stored theme is intentionally small: a preset id plus optional
 * overrides for the accent color and the heading/body fonts. Presets carry the
 * full look (background, text, accent, border, muted, font pair); the custom
 * controls only tweak the accent color and the two fonts on top of a preset.
 *
 * `resolveStorefrontTheme` turns the stored selection into concrete CSS values
 * + the Google Fonts <link> href. The educational shell applies the result as
 * CSS variables (--store-bg, --store-text, --store-accent, --store-border,
 * --store-muted, --font-heading, --font-body) which cascade to every child.
 */

export interface StorefrontTheme {
  /** Published Custom Store choices, shared by the storefront and portal. */
  customAccent?: 'blue' | 'green' | 'plum';
  customFont?: 'sans' | 'serif';
  logoUrl?: string;
  darkLogoUrl?: string;
  faviconUrl?: string;
  headline?: string;
  description?: string;
  presetId?: string;
  accentColor?: string;   // hex override for the preset accent
  headingFont?: string;   // HEADING_FONTS key
  bodyFont?: string;      // BODY_FONTS key
}

interface FontOption {
  key: string;
  label: string;
  /** CSS font-family stack. */
  family: string;
  /** Google Fonts family spec (the `family=` value), or null for system fonts. */
  googleFamily: string | null;
}

export const HEADING_FONTS: FontOption[] = [
  { key: 'fraunces', label: 'Fraunces', family: '"Fraunces", Georgia, serif', googleFamily: 'Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600' },
  { key: 'lora', label: 'Lora', family: '"Lora", Georgia, serif', googleFamily: 'Lora:wght@500;600;700' },
  { key: 'cormorant', label: 'Cormorant Garamond', family: '"Cormorant Garamond", Georgia, serif', googleFamily: 'Cormorant+Garamond:wght@500;600;700' },
  { key: 'playfair', label: 'Playfair Display', family: '"Playfair Display", Georgia, serif', googleFamily: 'Playfair+Display:wght@500;600;700' },
  { key: 'space-grotesk', label: 'Space Grotesk', family: '"Space Grotesk", system-ui, sans-serif', googleFamily: 'Space+Grotesk:wght@500;600;700' },
  { key: 'inter-tight', label: 'Inter Tight', family: '"Inter Tight", system-ui, sans-serif', googleFamily: 'Inter+Tight:wght@500;600;700' },
];

export const BODY_FONTS: FontOption[] = [
  { key: 'inter', label: 'Inter', family: '"Inter", system-ui, sans-serif', googleFamily: 'Inter:wght@400;500;600' },
  { key: 'work-sans', label: 'Work Sans', family: '"Work Sans", system-ui, sans-serif', googleFamily: 'Work+Sans:wght@400;500;600' },
  { key: 'nunito-sans', label: 'Nunito Sans', family: '"Nunito Sans", system-ui, sans-serif', googleFamily: 'Nunito+Sans:wght@400;500;600' },
  { key: 'source-sans', label: 'Source Sans 3', family: '"Source Sans 3", system-ui, sans-serif', googleFamily: 'Source+Sans+3:wght@400;500;600' },
];

export interface StorefrontPreset {
  id: string;
  label: string;
  description: string;
  bg: string;
  text: string;
  accent: string;
  /** Foreground used on top of the accent (e.g. button text, badge text). */
  accentForeground: string;
  border: string;
  muted: string;
  headingFontKey: string;
  bodyFontKey: string;
}

export const STOREFRONT_PRESETS: StorefrontPreset[] = [
  {
    id: 'apothecary',
    label: 'Apothecary',
    description: 'Warm cream + forest green, classic serif. The original look.',
    bg: '#faf8f4', text: '#1a1612', accent: '#2d4a3a', accentForeground: '#ffffff',
    border: '#e8e2d6', muted: '#8a8175',
    headingFontKey: 'fraunces', bodyFontKey: 'inter',
  },
  {
    id: 'clinical',
    label: 'Clinical',
    description: 'Clean white + medical blue, modern sans. Crisp and trustworthy.',
    bg: '#ffffff', text: '#0f172a', accent: '#2563eb', accentForeground: '#ffffff',
    border: '#e2e8f0', muted: '#64748b',
    headingFontKey: 'inter-tight', bodyFontKey: 'inter',
  },
  {
    id: 'wellness',
    label: 'Wellness',
    description: 'Soft beige + sage, elegant serif. Spa-like and calming.',
    bg: '#f6f4ee', text: '#2a2520', accent: '#5f7a5a', accentForeground: '#ffffff',
    border: '#e6ddd0', muted: '#8d8275',
    headingFontKey: 'cormorant', bodyFontKey: 'nunito-sans',
  },
  {
    id: 'midnight',
    label: 'Midnight',
    description: 'Cool off-white + deep violet, geometric sans. Bold and contemporary.',
    bg: '#f5f5f7', text: '#18181b', accent: '#6d28d9', accentForeground: '#ffffff',
    border: '#e4e4e7', muted: '#71717a',
    headingFontKey: 'space-grotesk', bodyFontKey: 'work-sans',
  },
  {
    id: 'ironside',
    label: 'Ironside',
    description: 'Cool slate + bold red, strong geometric type. Tuned for men’s health.',
    bg: '#f4f4f5', text: '#18181b', accent: '#b91c1c', accentForeground: '#ffffff',
    border: '#e4e4e7', muted: '#52525b',
    headingFontKey: 'space-grotesk', bodyFontKey: 'inter',
  },
  {
    id: 'blush',
    label: 'Blush',
    description: 'Soft rose + warm cream, elegant serif. Gentle, tuned for women’s health.',
    bg: '#fdf6f4', text: '#3b2d2e', accent: '#c2487a', accentForeground: '#ffffff',
    border: '#f0dfe2', muted: '#9a8388',
    headingFontKey: 'playfair', bodyFontKey: 'nunito-sans',
  },
];

export const DEFAULT_PRESET_ID = 'apothecary';

export function getPreset(presetId?: string): StorefrontPreset {
  return STOREFRONT_PRESETS.find(p => p.id === presetId) ?? STOREFRONT_PRESETS[0];
}

function findFont(options: FontOption[], key: string | undefined, fallbackKey: string): FontOption {
  return options.find(f => f.key === key) ?? options.find(f => f.key === fallbackKey) ?? options[0];
}

export interface ResolvedStorefrontTheme {
  bg: string;
  text: string;
  accent: string;
  accentForeground: string;
  border: string;
  muted: string;
  headingFamily: string;
  bodyFamily: string;
  /** Google Fonts stylesheet href to inject, or null if both fonts are system. */
  googleFontsHref: string | null;
  /** CSS custom properties to spread onto the storefront root element. */
  cssVars: Record<string, string>;
}

export function resolveStorefrontTheme(theme?: StorefrontTheme | null): ResolvedStorefrontTheme {
  if (theme?.customAccent && theme.customFont) {
    const accent = { blue: '#174bb5', green: '#246047', plum: '#723b72' }[theme.customAccent];
    const headingFamily = theme.customFont === 'serif' ? 'Georgia, serif' : 'Arial, sans-serif';
    const bodyFamily = 'Arial, sans-serif';
    if (accent) return {
      bg: '#ffffff', text: '#182536', accent, accentForeground: '#ffffff',
      border: '#dbe3ee', muted: '#42526a', headingFamily, bodyFamily, googleFontsHref: null,
      cssVars: {
        '--store-bg': '#ffffff', '--store-text': '#182536', '--store-accent': accent,
        '--store-accent-fg': '#ffffff', '--store-border': '#dbe3ee', '--store-muted': '#42526a',
        '--font-heading': headingFamily, '--font-body': bodyFamily,
      },
    };
  }
  const preset = getPreset(theme?.presetId);
  const heading = findFont(HEADING_FONTS, theme?.headingFont, preset.headingFontKey);
  const body = findFont(BODY_FONTS, theme?.bodyFont, preset.bodyFontKey);

  const accent = (theme?.accentColor && /^#[0-9a-fA-F]{6}$/.test(theme.accentColor))
    ? theme.accentColor
    : preset.accent;

  const families = [heading.googleFamily, body.googleFamily].filter((f): f is string => !!f);
  const googleFontsHref = families.length
    ? `https://fonts.googleapis.com/css2?${families.map(f => `family=${f}`).join('&')}&display=swap`
    : null;

  return {
    bg: preset.bg,
    text: preset.text,
    accent,
    accentForeground: preset.accentForeground,
    border: preset.border,
    muted: preset.muted,
    headingFamily: heading.family,
    bodyFamily: body.family,
    googleFontsHref,
    cssVars: {
      '--store-bg': preset.bg,
      '--store-text': preset.text,
      '--store-accent': accent,
      '--store-accent-fg': preset.accentForeground,
      '--store-border': preset.border,
      '--store-muted': preset.muted,
      '--font-heading': heading.family,
      '--font-body': body.family,
    },
  };
}
